/**
 * Client-side JWT payload decoder.
 *
 * Decodes the payload section of a JWT for UI display purposes ONLY.
 * This does NOT verify the signature — signature verification happens
 * on the server on every API request. Tampering with the stored token
 * will cause all API calls to fail with 401.
 *
 * Never trust decoded fields for authorization decisions in the UI.
 * Use them only for display (e.g. showing the active org name in the navbar).
 */
export interface DecodedToken {
  sub: number;
  email: string;
  permissions: string[];
  orgId: number | null;
  orgAlias: string | null;
  /** 1 = step-1 (no org), 2 = step-2 (org-scoped) */
  step: 1 | 2;
  exp?: number;
  iat?: number;
}

export function decodeJwt(token: string): DecodedToken | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    // Base64url → base64 → JSON
    const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = atob(payload);
    return JSON.parse(json) as DecodedToken;
  } catch {
    return null;
  }
}

/** Returns the org alias from the current JWT, or null if no org session. */
export function getOrgAliasFromToken(token: string | null): string | null {
  if (!token) return null;
  const decoded = decodeJwt(token);
  if (!decoded || decoded.step !== 2) return null;
  return decoded.orgAlias ?? null;
}

/** Returns true if the token is a step-2 org-scoped token. */
export function isOrgScopedToken(token: string | null): boolean {
  if (!token) return false;
  const decoded = decodeJwt(token);
  return decoded?.step === 2;
}
